<script setup lang="ts">
import { api } from '@/api';
import { TaskItemDtoTypeEnum, type TaskItemDto } from '@/api/generator';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useLoading } from '@/utils/useLoading';
import { useToken } from '@/utils/tokens/tokens';
import {
  validateDoseRates,
  validateFieldEdges,
  validateParameters,
  validateEvaluationScopes,
  taskBackendValidation,
  taskTypeLabel,
} from '@/views/tasks/utils.ts';
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import TaskForm from './components/TaskForm.vue';
import { toCreateOrUpdateTaskItemDto } from './mapping';

interface TaskDetailsEditProps {
  id: string;
}

const pageSubTitle = computed(() => taskTypeLabel(values.taskType));

const props = defineProps<TaskDetailsEditProps>();

const router = useRouter();

const { action: loadTask, loading } = useLoading(async () => {
  const { result } = await api.taskItemsApi.getTaskItem(
    {
      id: props.id,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    resetForm({
      values: result,
    });
  }
}, true);

const { form } = useVeeForm<TaskItemDto>({
  schemaName: 'TaskItemDto',
  mode: 'edit',
  customValidateFunctions: [
    taskBackendValidation,
    validateFieldEdges,
    validateDoseRates,
    validateParameters,
    validateEvaluationScopes,
  ],
  options: {
    keepValuesOnUnmount: true, // keep values when switching between tabs
  },
});

const { handleSubmit, resetForm, values } = form;

const token = useToken({
  id: props.id,
  $type:
    TaskItemDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTaskItemComponentDtoTaskItemDtoPtwPtw100AppZeroApplicationShared,
});

onMounted(token.request);

token.on.initialized(async () => await loadTask());

token.on.inUse(async () => {
  await router.push({ name: routenames.tasks.overview });
});

const submit = handleSubmit(
  async (dto: TaskItemDto) => {
    const { success } = await api.taskItemsApi.updateTaskItem(
      {
        id: dto.id,
        createOrUpdateTaskItemDto: toCreateOrUpdateTaskItemDto(dto),
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      await router.push({
        name: routenames.tasks.overview,
      });
    }
  },
  {
    withReferenceCheck: true,
  },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :title="values.name" :subtitle="pageSubTitle" :titleId="values.iuid" />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.tasks.overview }" />
        <VeeSubmitButton class="ml-4" :text="$t('SaveAndClose')" />
      </template>

      <TaskForm />
    </BasePage>
  </form>
</template>
